import { useEditor, REMOVE_KEYFRAME, ADD_KEYFRAME, UPDATE_KEYFRAME, snapToFrame, useComposition } from '@/lib/kinema';
import type { Effect, EasingMode } from '@/lib/kinema';
import * as ContextMenu from '@radix-ui/react-context-menu';
import type { TimelineScale } from './useTimelineScale';
import { KeyframeMarker } from './KeyframeMarker';

interface EffectTrackProps {
  effect: Effect;
  scale: TimelineScale;
  layerStartTime: number;
  layerId: string;
  color: string;
  selectedKeyframes?: Set<string>;
  onKeyframeClick?: (layerId: string, property: string, time: number, addToSelection: boolean) => void;
  /** Called with pixel dx when a selected keyframe is being dragged (for multi-drag preview) */
  onKeyframeDragMove?: (dx: number) => void;
  /** Called with pixel dx when drag completes on a selected keyframe */
  onKeyframeDragEnd?: (dx: number) => void;
  /** Called when drag is cancelled (reset previews) */
  onKeyframeDragCancel?: () => void;
  /** Current drag dx from another track (for multi-drag preview of this track's selected keyframes) */
  groupDragDx?: number;
}

const EASING_OPTIONS: { value: EasingMode; label: string }[] = [
  { value: 'linear', label: 'Linear' },
  { value: 'ease-in', label: 'Ease In' },
  { value: 'ease-out', label: 'Ease Out' },
  { value: 'ease-in-out', label: 'Ease In Out' },
  { value: 'step', label: 'Step' },
];

export function EffectTrack({ effect, scale, layerStartTime, layerId, color, selectedKeyframes, onKeyframeClick, onKeyframeDragMove, onKeyframeDragEnd, onKeyframeDragCancel, groupDragDx }: EffectTrackProps) {
  const editor = useEditor();
  const comp = useComposition();
  const { timeToX, xToTime, pixelsPerSecond } = scale;
  const pixelsPerFrame = pixelsPerSecond / comp.frameRate;

  const handleSingleKeyframeDrag = (originalTime: number, dx: number) => {
    const originalX = timeToX(layerStartTime + originalTime);
    const rawTime = Math.max(0, xToTime(originalX + dx) - layerStartTime);
    const roundedTime = snapToFrame(rawTime, comp.frameRate);
    if (roundedTime === originalTime) return;

    const kf = effect.keyframes.find((k) => k.time === originalTime);
    if (!kf) return;

    editor.batch(() => {
      editor.dispatchCommand(REMOVE_KEYFRAME, {
        layerId,
        property: effect.property,
        time: originalTime,
      });
      editor.dispatchCommand(ADD_KEYFRAME, {
        layerId,
        property: effect.property,
        keyframe: { time: roundedTime, value: kf.value, easing: kf.easing },
      });
    });
  };

  const handleChangeEasing = (kfTime: number, newEasing: EasingMode) => {
    editor.dispatchCommand(UPDATE_KEYFRAME, {
      layerId,
      property: effect.property,
      time: kfTime,
      easing: newEasing,
    });
  };

  const handleDeleteKeyframe = (kfTime: number) => {
    editor.dispatchCommand(REMOVE_KEYFRAME, {
      layerId,
      property: effect.property,
      time: kfTime,
    });
  };

  return (
    <div
      style={{
        position: 'relative',
        height: 24,
        minWidth: scale.totalWidth,
      }}
    >
      {/* Line connecting keyframes */}
      {effect.keyframes.length >= 2 && (
        <div
          style={{
            position: 'absolute',
            left: timeToX(layerStartTime + effect.keyframes[0].time),
            width: timeToX(
              effect.keyframes[effect.keyframes.length - 1].time - effect.keyframes[0].time,
            ),
            top: '50%',
            height: 2,
            background: `${color}40`,
            transform: 'translateY(-50%)',
          }}
        />
      )}

      {/* Keyframe diamonds with context menu */}
      {effect.keyframes.map((kf, i) => {
        const kfKey = `${layerId}:${effect.property}:${kf.time}`;
        const isSelected = selectedKeyframes?.has(kfKey) ?? false;
        const baseX = timeToX(layerStartTime + kf.time);
        // Apply group drag offset to selected keyframes (but not the one being dragged — it moves itself)
        const displayX = isSelected && groupDragDx ? baseX + groupDragDx : baseX;
        return (
          <KeyframeContextMenu
            key={`${kf.time}-${i}`}
            easing={kf.easing}
            onChangeEasing={(easing) => handleChangeEasing(kf.time, easing)}
            onDelete={() => handleDeleteKeyframe(kf.time)}
          >
            <KeyframeMarker
              x={displayX}
              selected={isSelected}
              color={color}
              easing={kf.easing}
              pixelsPerFrame={pixelsPerFrame}
              onDragEnd={isSelected
                ? (dx) => onKeyframeDragEnd?.(dx)
                : (dx) => handleSingleKeyframeDrag(kf.time, dx)
              }
              onDragMove={isSelected ? onKeyframeDragMove : undefined}
              onDragCancel={isSelected ? onKeyframeDragCancel : undefined}
              onClick={(e) => onKeyframeClick?.(layerId, effect.property, kf.time, e.metaKey || e.ctrlKey || e.shiftKey)}
            />
          </KeyframeContextMenu>
        );
      })}
    </div>
  );
}

// ─── Keyframe Context Menu ────────────────────────────────

function KeyframeContextMenu({
  children,
  easing,
  onChangeEasing,
  onDelete,
}: {
  children: React.ReactNode;
  easing: EasingMode;
  onChangeEasing: (easing: EasingMode) => void;
  onDelete: () => void;
}) {
  return (
    <ContextMenu.Root>
      <ContextMenu.Trigger asChild>
        {children}
      </ContextMenu.Trigger>
      <ContextMenu.Portal>
        <ContextMenu.Content style={menuContentStyle}>
          <ContextMenu.Label style={menuLabelStyle}>Easing</ContextMenu.Label>
          {EASING_OPTIONS.map((opt) => (
            <ContextMenu.CheckboxItem
              key={opt.value}
              checked={easing === opt.value}
              onCheckedChange={() => onChangeEasing(opt.value)}
              style={menuItemStyle}
            >
              <ContextMenu.ItemIndicator style={{ position: 'absolute', left: 8 }}>
                <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
                  <circle cx="5" cy="5" r="3" />
                </svg>
              </ContextMenu.ItemIndicator>
              <span style={{ paddingLeft: 20 }}>{opt.label}</span>
            </ContextMenu.CheckboxItem>
          ))}
          <ContextMenu.Separator style={menuSepStyle} />
          <ContextMenu.Item
            onSelect={onDelete}
            style={{ ...menuItemStyle, color: '#ef4444' }}
          >
            <span style={{ paddingLeft: 20 }}>Delete Keyframe</span>
          </ContextMenu.Item>
        </ContextMenu.Content>
      </ContextMenu.Portal>
    </ContextMenu.Root>
  );
}

const menuContentStyle: React.CSSProperties = {
  background: '#ffffff',
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  padding: '4px 0',
  boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
  minWidth: 160,
  zIndex: 10000,
  fontFamily: 'system-ui, sans-serif',
  fontSize: 12,
};

const menuLabelStyle: React.CSSProperties = {
  padding: '4px 12px',
  fontSize: 10,
  color: '#94a3b8',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
};

const menuItemStyle: React.CSSProperties = {
  padding: '6px 12px',
  cursor: 'pointer',
  outline: 'none',
  color: '#334155',
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
};

const menuSepStyle: React.CSSProperties = {
  height: 1,
  background: '#e2e8f0',
  margin: '4px 0',
};
